import { useQuery } from '@tanstack/react-query'
import { PaytrieAPI } from '@paytrie/client'

import {
  getOtcRequest,
  getWireInstructions,
  listOtcRequests,
} from '@/requests/otc'

export const otcKeys = {
  all: ['otc'] as const,
  list: () => [...otcKeys.all, 'list'] as const,
  detail: (id: string) => [...otcKeys.all, 'detail', id] as const,
  wireInstructions: () => [...otcKeys.all, 'wire-instructions'] as const,
  linkedTransaction: (id: string) =>
    [...otcKeys.all, 'linked-transaction', id] as const,
}

export const useOtcRequests = () =>
  useQuery({
    queryKey: otcKeys.list(),
    queryFn: listOtcRequests,
    staleTime: 30 * 1000,
  })

export const useOtcRequest = (id: string | undefined) =>
  useQuery({
    queryKey: otcKeys.detail(id ?? ''),
    queryFn: () => getOtcRequest(id as string),
    enabled: !!id,
    refetchInterval: 15 * 1000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number }).status
      if (status !== undefined && status >= 400 && status < 500) return false
      return failureCount < 3
    },
  })

export const useWireInstructions = () =>
  useQuery({
    queryKey: otcKeys.wireInstructions(),
    queryFn: getWireInstructions,
    staleTime: 60 * 60 * 1000,
  })

export const useLinkedTransaction = (transactionId: string | null) =>
  useQuery({
    queryKey: otcKeys.linkedTransaction(transactionId ?? ''),
    queryFn: async () => {
      const res = await PaytrieAPI.getTransaction(transactionId as string)
      if (res.status === 200 && res.data.success) {
        return res.data.data
      }
      throw new Error(`Failed to load transaction (status ${res.status})`)
    },
    enabled: !!transactionId,
    retry: false,
    staleTime: 30 * 1000,
  })
